import nanoid from 'nanoid';
import Model from '@newspaces/zero-base/Model';
import Organisation from './models/Organisation.js';
import { A as AccessSite } from './AccessSite-5abc043f.js';
import { n as normalizeFilter, A as AccessGroup } from './AccessGroup-4a811995.js';
import AccessConfiguration from './models/AccessConfiguration.js';
import { b as canUpdateDocuments, c as canInsertDocument, a as canRemoveDocuments } from './canRemoveDocuments-3b897618.js';
import generate from 'nanoid/generate';
import DateTime from '@newspaces/zero-base/DateTime';
import AccessVendor from './models/AccessVendor.js';
import { BlueHardwareType } from '@blueid/access-proto';
import './i18n.js';
import i18n from '@newspaces/zero-base/i18n';

const queryDocument$1 = (filter, _, Runtime) => {
  if (!filter.organisation) {
    return false;
  }
  const organisationUser = Runtime.getOrganisationData(filter.organisation);
  if (!organisationUser) {
    return false;
  }
  const hasScope = (scope) => {
    return Runtime.userHasAllScopes(
      filter.organisation.toString(),
      null,
      scope
    );
  };
  if (!hasScope("accessObject.read")) {
    return false;
  }
  return normalizeFilter(Runtime, filter, "site");
};

const editDocuments$1 = async (filter, changeSet, _, Runtime) => {
  const { organisation: organisationId } = filter;
  if (!organisationId) {
    return false;
  }
  const hasScope = (scope) => {
    return Runtime.userHasAllScopes(
      filter.organisation.toString(),
      null,
      scope
    );
  };
  if (!hasScope("accessObject.edit")) {
    return false;
  }
  return canUpdateDocuments(Runtime, filter, changeSet, "accessCredentials", "site");
};

const insertDocuments = (documents, _, Runtime) => {
  const clearedDocuments = documents.filter((document) => {
    const { organisation: organisationId } = document;
    if (!organisationId) {
      return false;
    }
    const hasScope = (scope) => {
      return Runtime.userHasAllScopes(
        organisationId.toString(),
        null,
        scope
      );
    };
    if (!hasScope("accessObject.insert")) {
      return false;
    }
    return canInsertDocument(Runtime, document, "site");
  });
  if (!clearedDocuments.length) {
    return false;
  }
  return clearedDocuments;
};

const removeDocuments = async (filter, Runtime) => {
  const { organisation: organisationId } = filter;
  if (!organisationId) {
    return false;
  }
  if (!Runtime.userHasAllScopes(organisationId.toString(), null, "accessObject.remove")) {
    return false;
  }
  return canRemoveDocuments(Runtime, filter, "accessObjects", "site");
};

const Permissions$1 = {
  insert: [
    {
      who: "owner"
    },
    {
      query: insertDocuments
    }
  ],
  read: [
    {
      who: "owner"
    },
    {
      query: queryDocument$1
    }
  ],
  edit: [
    {
      who: "owner"
    },
    {
      query: editDocuments$1
    }
  ],
  remove: [
    {
      who: "owner"
    },
    {
      query: removeDocuments
    }
  ]
};

class AccessObject extends Model {
  static CollectionName = "accessObjects";
  static Permissions = Permissions$1;
  static Fields = {
    // The organisation can be null when the device was not claimed yet
    organisation: {
      type: Organisation,
      serialize: "insert"
    },
    site: {
      type: AccessSite,
      required: true,
      serialize: "insert",
      description: "The site this object belongs too"
    },
    objectId: {
      type: Number,
      serialize: false,
      description: "The unique ID of this object. Automatically generated by the system",
      unique: "local"
    },
    configId: {
      type: String,
      max: 8,
      min: 8,
      required: true,
      default: () => nanoid(8),
      description: "The current id for the configuration on this specific object"
    },
    configuration: {
      type: AccessConfiguration,
      required: ({ configuration }) => {
        if (Model.isRootModel(configuration)) {
          return false;
        }
        return true;
      },
      serialize: "insert"
    },
    groups: {
      type: [AccessGroup],
      description: "All groups this object is part of",
      serialize: "insert",
      validate: ({ groups }) => {
        if (groups?.length > 10) {
          return "maxTenGroups";
        }
        return null;
      }
    },
    groupNames: {
      type: [String],
      description: "Stored names in order of the groups, if not set will be assigned on storing",
      serialize: "insert"
    },
    devices: {
      type: [Model],
      serialize: false,
      description: "One or more devices attached to this object"
    },
    deviceIds: {
      type: [String],
      description: "The ids of the devices assigned",
      serialize: false
    },
    name: {
      type: String,
      required: true,
      max: 255,
      description: "The name of the object"
    },
    description: {
      type: String,
      description: "Optional description for this object for quicker identification"
    }
  };
  constructor(data) {
    super(AccessObject.Fields, data);
  }
}
Model.declareModel(AccessObject);

const hardwareTypes = Object.keys(BlueHardwareType).filter((v) => Number.isNaN(Number(v)));

class AccessDeviceHardware extends Model {
  static Fields = {
    vendor: {
      type: AccessVendor,
      required: true
    },
    vendorName: {
      type: String,
      required: true,
      serialize: "insert",
      description: "The human readable name of the vendor of the device"
    },
    type: {
      type: String,
      required: true,
      serialize: "insert",
      enum: hardwareTypes,
      description: "The type code of the hardware to identify the type of device"
    },
    version: {
      type: Number,
      required: true,
      serialize: "insert",
      description: "The vendor-specific version of the hardware"
    },
    serialNumber: {
      type: String,
      required: true,
      serialize: "insert",
      description: "The vendor-specific serial number of the device"
    },
    name: {
      type: String,
      required: true,
      serialize: "insert",
      description: "The human readable vendor-specific name of the hardware"
    },
    meta: {
      type: Object,
      serialize: "insert",
      description: "Optional additionally meta data from vendor"
    }
  };
  constructor(data) {
    super(AccessDeviceHardware.Fields, data);
  }
  get info() {
    const parts = [];
    if (this.name) {
      parts.push(this.name);
    }
    if (this.type) {
      parts.push(`(${this.type})`);
    }
    return parts.join(" ");
  }
}

class AccessDeviceSystemStatus extends Model {
  static Fields = {
    configId: {
      type: String,
      max: 8,
      min: 8,
      serialize: false,
      description: "The actual config id that is on this device"
    },
    batteryLevel: {
      type: Number,
      serialize: false,
      description: "The last measured battery level of the device"
    },
    applicationVersion: {
      type: Number,
      serialize: false,
      description: "The current application version on the device"
    },
    localTime: {
      type: DateTime,
      serialize: false,
      description: "The current local time on the device when the status was updated"
    },
    blacklistMaxEntriesCount: {
      type: Number,
      serialize: false,
      description: "Maximum number of blacklist entries the device can take"
    },
    blacklistEntriesCount: {
      type: Number,
      serialize: false,
      description: "Number of blacklist entries on the device"
    },
    eventLogMaxEntriesCount: {
      type: Number,
      serialize: false,
      description: "Maximum number of event entries the device can take"
    },
    eventLogEntriesCount: {
      type: Number,
      serialize: false,
      description: "Number of blacklist entries on the device"
    },
    systemLogMaxEntriesCount: {
      type: Number,
      serialize: false,
      description: "Maximum number of system log entries the device can take"
    },
    systemLogEntriesCount: {
      type: Number,
      serialize: false,
      description: "Number of system log entries on the device"
    }
  };
  parent;
  constructor(data, parent) {
    super(AccessDeviceSystemStatus.Fields, data);
    this.parent = parent;
  }
  get info() {
    if (!this.parent?.systemStatusUpdatedAt) {
      return "N/A";
    }
    return i18n().formatDate(this.parent.systemStatusUpdatedAt);
  }
}

const queryDocument = (filter, _, Runtime) => {
  if (!filter.organisation) {
    return false;
  }
  const organisationUser = Runtime.getOrganisationData(filter.organisation);
  if (!organisationUser) {
    return false;
  }
  const hasScope = (scope) => {
    return Runtime.userHasAllScopes(
      filter.organisation.toString(),
      null,
      scope
    );
  };
  if (!hasScope("accessDevice.read")) {
    return false;
  }
  return normalizeFilter(Runtime, filter, "site");
};

const editDocuments = async (filter, changeSet, _, Runtime) => {
  const { organisation: organisationId } = filter;
  if (!organisationId) {
    return false;
  }
  const hasScope = (scope) => {
    return Runtime.userHasAllScopes(
      filter.organisation.toString(),
      null,
      scope
    );
  };
  if (!hasScope("accessDevice.edit")) {
    return false;
  }
  return canUpdateDocuments(Runtime, filter, changeSet, "accessDevices", "site");
};

const Permissions = {
  insert: false,
  read: [
    {
      who: "owner"
    },
    {
      query: queryDocument
    }
  ],
  edit: [
    {
      who: "owner"
    },
    {
      query: editDocuments
    }
  ],
  remove: false
};

class AccessDevice extends Model {
  static CollectionName = "accessDevices";
  static Permissions = Permissions;
  static Fields = {
    // The organisation can be null when the device was not claimed yet
    organisation: {
      type: Organisation,
      serialize: "insert"
    },
    status: {
      type: String,
      enum: ["created", "claimed", "active", "requireUpdate", "disabled"],
      required: true
    },
    site: {
      type: AccessSite,
      serialize: false,
      description: "The site this device is attached to"
    },
    object: {
      // ugly but will be set in AccessObject to avoid circular deps
      type: Model,
      serialize: false,
      description: "The object this device is attached to"
    },
    objectName: {
      type: String,
      required: ({ object }) => !!object,
      serialize: false,
      description: "The human readable name of the object attached to"
    },
    deviceId: {
      type: String,
      max: 8,
      min: 8,
      required: true,
      unique: "global",
      serialize: "insert",
      default: () => generate("0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz", 8),
      description: "The unique device id for this device"
    },
    description: {
      type: String,
      description: "Optional description for this device for quicker identification"
    },
    hardware: {
      type: AccessDeviceHardware,
      serialize: "insert",
      description: "Details about the specific hardware of the device"
    },
    claimedAt: {
      type: DateTime,
      serialize: false,
      description: "The timestamp when the device was claimed"
    },
    configId: {
      type: String,
      max: 8,
      min: 8,
      serialize: false,
      description: "The config id that is expected to be on this device"
    },
    systemStatus: {
      type: AccessDeviceSystemStatus,
      serialize: false,
      description: "The last received status of the device"
    },
    systemStatusUpdatedAt: {
      type: DateTime,
      serialize: false,
      description: "When the system status of this device was updated the last time"
    },
    // -- internal field to store the previous deviceId when the device was reprovisioned
    reprovisionFromDeviceId: {
      type: String,
      serialize: "insert"
    }
  };
  constructor(data) {
    super(AccessDevice.Fields, data);
  }
  static StatusColors = {
    created: "neutral",
    claimed: "primary",
    active: "success",
    requireUpdate: "warning",
    revoked: "error"
  };
}
Model.declareModel(AccessDevice);

export { AccessObject as A, AccessDevice as a, hardwareTypes as h };
//# sourceMappingURL=AccessDevice-68531278.js.map
